import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { portalApi } from '../services/portalApi';
import { queryKeys } from './queryKeys';
import { adaptCall } from '../services/callAdapter';
import { useApiMutation } from '../hooks/useApiMutation';

const RANGE_PARAMS = { all: undefined, today: 'day', 7: '7d', 30: '30d' };

const adaptPage = (page) => ({ ...page, docs: (page?.docs || []).map((c) => adaptCall(c)) });

export function useCallLog({
  type = 'all',
  range = 'all',
  scope,
  search = '',
  direction = 'all',
  handledBy = 'all',
  page = 1,
  pageSize = 10,
  enabled = true
} = {}) {
  const params = {
    page,
    limit: pageSize,
    search: search || undefined,
    range: range in RANGE_PARAMS ? RANGE_PARAMS[range] : range,
    scope,
    bucket: type === 'all' ? undefined : type,
    direction: direction === 'all' ? undefined : direction,
    handledBy: handledBy === 'all' ? undefined : handledBy
  };
  const query = useQuery({
    queryKey: queryKeys.calls.list(params),
    queryFn: () => portalApi.calls(params),
    select: adaptPage,
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000
  });
  return {
    rows: query.data?.docs || [],
    total: query.data?.totalDocs || 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch
  };
}

const adaptDetail = (r) => adaptCall(r.call);

export function useCallQuery(id) {
  const query = useQuery({
    queryKey: queryKeys.calls.detail(id),
    queryFn: () => portalApi.call(id),
    select: adaptDetail,
    enabled: Boolean(id)
  });
  return { ...query, call: query.data || null };
}

export function useCallRecordingQuery(id) {
  return useQuery({
    queryKey: queryKeys.calls.recording(id),
    queryFn: () => portalApi.callRecording(id),
    enabled: Boolean(id),
    staleTime: 20 * 60_000,
    retry: false
  });
}

const patchCall = (id, patch) => (record) => (record?._id === id ? { ...record, ...patch } : record);
const patchDetail = (id, patch) => (data) => ({ ...data, call: patchCall(id, patch)(data.call) });

export function useToggleReviewed() {
  return useApiMutation({
    mutationFn: ({ id, reviewed }) => portalApi.updateCall(id, { reviewed }),
    optimistic: ({ id, reviewed }) => [
      {
        key: queryKeys.calls.lists(),
        update: (data) => ({ ...data, docs: (data.docs || []).map(patchCall(id, { reviewed })) })
      },
      { key: queryKeys.calls.detail(id), update: patchDetail(id, { reviewed }) }
    ],
    invalidate: [queryKeys.calls.all],
    error: 'Could not update this call'
  });
}

export function useResolveCall() {
  return useApiMutation({
    mutationFn: (id) => portalApi.resolveCall(id),
    invalidate: [queryKeys.calls.all],
    success: 'Marked as resolved',
    error: 'Could not resolve this call'
  });
}
